import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Pencil, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { admin } from '@/lib/api';
import { toast } from 'sonner';

const emptyForm = () => ({
  name: '',
  slug: '',
  description: '',
  parent_id: '',
});

const AdminCategoriesTab = ({ categories, setDeleteDialog, hasPermission, fetchData }) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const parentOptions = useMemo(() => {
    return (categories || []).filter((c) => !editingId || c.id !== editingId);
  }, [categories, editingId]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (category) => {
    setEditingId(category.id);
    setForm({
      name: category.name || '',
      slug: category.slug || '',
      description: category.description || '',
      parent_id: category.parent_id || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) {
      toast.error(t('admin.categoryNameRequired', 'Kategori adı gerekli'));
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name,
        description: form.description.trim() || undefined,
        parent_id: form.parent_id || undefined,
      };
      const slug = form.slug.trim();
      if (slug) payload.slug = slug;

      if (editingId) {
        await admin.updateCategory(editingId, {
          ...payload,
          parent_id: form.parent_id || null,
        });
        toast.success(t('admin.categoryUpdated', 'Kategori güncellendi'));
      } else {
        await admin.createCategory(payload);
        toast.success(t('admin.categoryCreated', 'Kategori eklendi'));
      }
      setDialogOpen(false);
      setEditingId(null);
      setForm(emptyForm());
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('admin.categorySaveError', 'Kayıt başarısız'));
    } finally {
      setSaving(false);
    }
  };

  const canEdit = hasPermission('settings.categories');

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>
          {t('filters.categories')} ({categories.length})
        </CardTitle>
        {canEdit && (
          <Button type="button" size="sm" onClick={openCreate} data-testid="category-add-btn">
            <Plus className="w-4 h-4 mr-1" />
            {t('admin.addCategory', 'Yeni kategori')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('auth.name')}</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>{t('product.description')}</TableHead>
              {canEdit && (
                <TableHead className="text-right">{t('common.actions', 'İşlemler')}</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.map((category) => (
              <TableRow key={category.id}>
                <TableCell className="font-medium">{category.name}</TableCell>
                <TableCell className="text-muted-foreground font-mono text-sm">{category.slug}</TableCell>
                <TableCell className="text-muted-foreground max-w-md truncate">
                  {category.description || '—'}
                </TableCell>
                {canEdit && (
                  <TableCell className="text-right space-x-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(category)}
                      title={t('common.edit', 'Düzenle')}
                      data-testid={`category-edit-${category.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        setDeleteDialog({
                          open: true,
                          type: 'category',
                          id: category.id,
                          name: category.name,
                        })
                      }
                      title={t('common.delete')}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingId
                  ? t('admin.editCategory', 'Kategori düzenle')
                  : t('admin.addCategory', 'Yeni kategori')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cat-name">{t('auth.name')} *</Label>
                <Input
                  id="cat-name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                  data-testid="category-form-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cat-slug">Slug</Label>
                <Input
                  id="cat-slug"
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                  placeholder={t('admin.categorySlugHint', 'Boş bırakılırsa isimden üretilir')}
                  data-testid="category-form-slug"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cat-desc">{t('product.description')}</Label>
                <Textarea
                  id="cat-desc"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  data-testid="category-form-description"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.parentCategory', 'Üst kategori')}</Label>
                <Select
                  value={form.parent_id || '__none__'}
                  onValueChange={(v) => setForm((f) => ({ ...f, parent_id: v === '__none__' ? '' : v }))}
                >
                  <SelectTrigger data-testid="category-form-parent">
                    <SelectValue placeholder={t('admin.noParent', 'Yok (üst seviye)')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t('admin.noParent', 'Yok (üst seviye)')}</SelectItem>
                    {parentOptions.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? '…' : t('common.save', 'Kaydet')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default AdminCategoriesTab;
